import Quill from 'quill';
import 'quill/dist/quill.snow.css';

/**
 * RichTextEditor - A wrapper around Quill.js for consistent rich text editing
 */
class RichTextEditor {
    constructor(container, options = {}) {
        this.container = container;
        this.defaultOptions = {
            theme: 'snow',
            placeholder: options.placeholder || 'Enter text...',
            modules: {
                toolbar: [
                    ['bold', 'italic'],
                    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                    ['link'],
                    [{ 'header': [1, 2, 3, false] }],
                    ['clean'] // remove formatting button
                ]
            },
            formats: ['bold', 'italic', 'list', 'link', 'header']
        };
        
        this.options = { ...this.defaultOptions, ...options };
        this.quill = null;
        this.init();
    }

    init() {
        // Create editor container if it doesn't exist
        if (typeof this.container === 'string') {
            this.container = document.querySelector(this.container);
        }
        
        if (!this.container) {
            console.error('RichTextEditor: Container not found');
            return;
        }

        // Initialize Quill
        this.quill = new Quill(this.container, this.options);
        
        return this;
    }

    /**
     * Get the editor content as HTML
     */
    getHTML() {
        if (!this.quill) return '';
        return this.quill.getSemanticHTML();
    }

    /**
     * Get the editor content as plain text
     */
    getText() {
        if (!this.quill) return '';
        return this.quill.getText();
    }

    /**
     * Set the editor content
     */
    setHTML(html) {
        if (!this.quill) return;
        const delta = this.quill.clipboard.convert({html})

        this.quill.setContents(delta, 'silent')
    }

    /**
     * Set plain text content
     */
    setText(text) {
        if (!this.quill) return;
        this.quill.setText(text);
    }

    /**
     * Get the Quill instance for advanced operations
     */
    getEditor() {
        return this.quill;
    }

    /**
     * Add change listener
     */
    onChange(callback) {
        if (!this.quill) return;
        this.quill.on('text-change', callback);
    }

    /**
     * Enable/disable the editor
     */
    enable(enabled = true) {
        if (!this.quill) return;
        this.quill.enable(enabled);
    }

    /**
     * Destroy the editor instance
     */
    destroy() {
        if (this.quill) {
            // Get the container before destroying
            const container = this.quill.container;
            // Remove Quill
            delete this.quill;
            // Clear the container
            if (container) {
                container.innerHTML = '';
            }
        }
    }
}

export default RichTextEditor;